/**
 * Timeline Utility Functions
 *
 * Pure functions for timeline calculations, snap detection, time formatting, etc.
 */

import type { TimelineAudioClip, TimelineClip, TimelineRenderConfig, TimelineState } from './types'
import type { TimelineAudioConfigDict } from '../../../../generated/nodeDataTypes'
import {
  audioKindForLegacyRole,
  fallbackAudioLabelForKind,
  isOriginalAudio,
  isLegacyTimelineAudioRole,
  isTimelineAudioKind,
  type LegacyTimelineAudioRole,
  type TimelineAudioKind,
} from './timelineAudioKind'
import { MAX_VOLUME_AMPLITUDE } from './volumeMapping'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'

const AUDIO_GAP_EPSILON_S = 0.001
const DEFAULT_TIMELINE_CLIP_DURATION_S = 5
export const MIN_VIDEO_EXPORT_DURATION_SECONDS = 1

/** Return true only when a finite, known export duration is below the minimum. */
export function isVideoExportDurationTooShort(
  durationSeconds: number | null | undefined,
): boolean {
  return (
    typeof durationSeconds === 'number' &&
    Number.isFinite(durationSeconds) &&
    durationSeconds < MIN_VIDEO_EXPORT_DURATION_SECONDS
  )
}

/**
 * Best-effort output duration for a persisted timeline render config.
 *
 * The explicit total duration contributes an output-duration floor. For older
 * configs that omit it, derive video and external-audio ends using the same
 * automatic placement rules as the worker. Unknown media duration remains
 * permissive unless another known media track already proves the export is
 * long enough.
 */
export function timelineRenderConfigDuration(
  config: TimelineRenderConfig | null | undefined,
): number | undefined {
  const timeline = config?.timeline
  if (!timeline) return undefined

  const declaredDuration = timeline.total_duration
  const usableDeclaredDuration =
    typeof declaredDuration === 'number' && Number.isFinite(declaredDuration)
      ? Math.max(0, declaredDuration)
      : undefined

  const mediaDuration = (item: {
    start_time?: number
    end_time?: number
    original_duration?: number
    speed?: number
  }): number | undefined => {
    const startTime = item.start_time ?? 0
    const endTime = item.end_time ?? item.original_duration
    const speed = item.speed ?? 1
    if (
      typeof startTime !== 'number' ||
      !Number.isFinite(startTime) ||
      typeof endTime !== 'number' ||
      !Number.isFinite(endTime) ||
      typeof speed !== 'number' ||
      !Number.isFinite(speed) ||
      speed <= 0
    ) {
      return undefined
    }
    return Math.max(0, endTime - Math.max(0, startTime)) / speed
  }

  let hasUnknownDuration = false
  let videoCumulativeDuration = 0
  let videoEnd = 0
  const clips = [...(timeline.clips ?? [])].sort(
    (left, right) => (left.order ?? 0) - (right.order ?? 0),
  )
  for (const clip of clips) {
    const effectiveDuration = mediaDuration(clip)
    if (effectiveDuration === undefined) {
      hasUnknownDuration = true
      continue
    }
    const startOffset =
      typeof clip.start_offset === 'number' && Number.isFinite(clip.start_offset)
        ? Math.max(0, clip.start_offset)
        : videoCumulativeDuration
    videoEnd = Math.max(videoEnd, startOffset + effectiveDuration)
    videoCumulativeDuration += effectiveDuration
  }

  const configuredAudioTracks =
    timeline.audio_tracks && timeline.audio_tracks.length > 0
      ? timeline.audio_tracks
      : timeline.audio
        ? [timeline.audio]
        : []
  const audioCumulativeDurationByTrack = new Map<number, number>()
  let audioEnd = 0
  for (const audioTrack of configuredAudioTracks) {
    const effectiveDuration = mediaDuration(audioTrack)
    if (effectiveDuration === undefined) {
      hasUnknownDuration = true
      continue
    }
    const trackIndex = audioTrack.track_index ?? 0
    const automaticStartOffset = audioCumulativeDurationByTrack.get(trackIndex) ?? 0
    const startOffset =
      typeof audioTrack.start_offset === 'number' && Number.isFinite(audioTrack.start_offset)
        ? Math.max(0, audioTrack.start_offset)
        : automaticStartOffset
    audioEnd = Math.max(audioEnd, startOffset + effectiveDuration)
    audioCumulativeDurationByTrack.set(
      trackIndex,
      Math.max(automaticStartOffset, startOffset + effectiveDuration),
    )
  }

  const knownDuration = Math.max(usableDeclaredDuration ?? 0, videoEnd, audioEnd)
  if (
    hasUnknownDuration &&
    usableDeclaredDuration === undefined &&
    knownDuration < MIN_VIDEO_EXPORT_DURATION_SECONDS
  ) {
    return undefined
  }
  return knownDuration
}

export function coerceTimelineDurationHint(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined
  }
  return value
}

export type TimelineDurationBasis = 'visual-stream' | 'source-timeline' | 'audio-stream' | 'legacy-container'

export interface TimelineDurationHint {
  id: string
  path: string
  duration?: number
  basis: TimelineDurationBasis
}

export interface EffectiveTimelineConfigResult {
  config: TimelineRenderConfig | undefined
  changed: boolean
  authoritativeVideoIds: string[]
  legacyVideoIds: string[]
  missingVideoIds: string[]
}

function isAuthoritativeDurationBasis(basis: TimelineDurationBasis): boolean {
  return basis === 'visual-stream' || basis === 'source-timeline' || basis === 'audio-stream'
}

function timelineDurationHintForEntry(
  entry: { id?: string; source_node_id?: string; source_path?: string },
  hints: readonly TimelineDurationHint[],
): TimelineDurationHint | undefined {
  const symbolicSourceId = entry.source_path?.match(/^([^./]+)\.out_[A-Za-z0-9_]+$/)?.[1]
  if (entry.source_path && symbolicSourceId === undefined) {
    return hints.find((hint) => hint.path === entry.source_path)
  }
  return hints.find((hint) =>
    hint.id === entry.source_node_id || hint.id === symbolicSourceId || hint.id === entry.id,
  )
}

function wireEntryDuration(item: {
  start_time?: number
  end_time?: number
  original_duration?: number
  speed?: number
}): number | undefined {
  const start = typeof item.start_time === 'number' && Number.isFinite(item.start_time)
    ? Math.max(0, item.start_time)
    : 0
  const end = coerceTimelineDurationHint(item.end_time) ?? coerceTimelineDurationHint(item.original_duration)
  const speed = coerceTimelineDurationHint(item.speed) ?? 1
  if (end === undefined || end <= start) return undefined
  return (end - start) / speed
}

function timelineMediaEnd(timeline: TimelineRenderConfig['timeline']): number | undefined {
  let hasUnknown = false
  let videoCursor = 0
  let end = 0
  for (const clip of [...(timeline.clips ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    const duration = wireEntryDuration(clip)
    if (duration === undefined) { hasUnknown = true; continue }
    const offset = typeof clip.start_offset === 'number' && Number.isFinite(clip.start_offset)
      ? Math.max(0, clip.start_offset)
      : videoCursor
    end = Math.max(end, offset + duration)
    videoCursor += duration
  }
  const audioTracks = timeline.audio_tracks?.length
    ? timeline.audio_tracks
    : timeline.audio ? [timeline.audio] : []
  const audioCursorByTrack = new Map<number, number>()
  for (const track of audioTracks) {
    const duration = wireEntryDuration(track)
    if (duration === undefined) { hasUnknown = true; continue }
    const trackIndex = track.track_index ?? 0
    const cursor = audioCursorByTrack.get(trackIndex) ?? 0
    const offset = typeof track.start_offset === 'number' && Number.isFinite(track.start_offset)
      ? Math.max(0, track.start_offset)
      : cursor
    end = Math.max(end, offset + duration)
    audioCursorByTrack.set(trackIndex, Math.max(cursor, offset + duration))
  }
  for (const overlay of timeline.text_overlays ?? []) {
    const start = typeof overlay.start_seconds === 'number' && Number.isFinite(overlay.start_seconds)
      ? Math.max(0, overlay.start_seconds)
      : 0
    const duration = coerceTimelineDurationHint(overlay.duration_seconds)
    if (duration !== undefined) end = Math.max(end, start + duration)
  }
  return hasUnknown && end === 0 ? undefined : end
}

/** Derive the single timeline snapshot consumed by preview and export. */
export function deriveEffectiveTimelineConfig(
  timelineConfig: TimelineRenderConfig | null | undefined,
  videoHints: readonly TimelineDurationHint[],
  audioHints: readonly TimelineDurationHint[] = [],
): EffectiveTimelineConfigResult {
  if (!timelineConfig?.timeline) {
    return { config: undefined, changed: false, authoritativeVideoIds: [], legacyVideoIds: [], missingVideoIds: [] }
  }
  const originalMediaEnd = timelineMediaEnd(timelineConfig.timeline)
  const authoritativeVideoIds: string[] = []
  const legacyVideoIds: string[] = []
  const missingVideoIds: string[] = []
  let authoritativeVideoPresent = false
  const clips = (timelineConfig.timeline.clips ?? []).map((clip, index) => {
    const id = clip.id ?? clip.source_node_id ?? clip.source_path ?? `video-${index + 1}`
    const hint = timelineDurationHintForEntry(clip, videoHints)
    const hintedDuration = coerceTimelineDurationHint(hint?.duration)
    const savedDuration = coerceTimelineDurationHint(clip.original_duration) ?? coerceTimelineDurationHint(clip.end_time)
    const authoritative = hintedDuration !== undefined && hint !== undefined && isAuthoritativeDurationBasis(hint.basis)
    if (authoritative) {
      authoritativeVideoPresent = true
      authoritativeVideoIds.push(id)
      const start = typeof clip.start_time === 'number' && Number.isFinite(clip.start_time)
        ? Math.max(0, clip.start_time)
        : 0
      const savedEnd = coerceTimelineDurationHint(clip.end_time) ?? savedDuration ?? hintedDuration
      const savedWasFullSource = clip.end_time === undefined || (
        coerceTimelineDurationHint(clip.original_duration) !== undefined &&
        Math.abs(savedEnd - (clip.original_duration ?? savedEnd)) <= 1e-6
      )
      const end = savedWasFullSource ? hintedDuration : Math.min(savedEnd, hintedDuration)
      if (end <= start) { missingVideoIds.push(id); return clip }
      return { ...clip, original_duration: hintedDuration, end_time: end }
    }
    if (savedDuration !== undefined) { legacyVideoIds.push(id); return clip }
    if (hintedDuration !== undefined) {
      legacyVideoIds.push(id)
      return { ...clip, original_duration: hintedDuration, end_time: hintedDuration }
    }
    missingVideoIds.push(id)
    return clip
  })
  const applyAudioHint = (track: TimelineAudioConfigDict): TimelineAudioConfigDict => {
    const hint = timelineDurationHintForEntry(track, audioHints)
    const hintedDuration = coerceTimelineDurationHint(hint?.duration)
    const savedDuration = coerceTimelineDurationHint(track.original_duration) ?? coerceTimelineDurationHint(track.end_time)
    if (hintedDuration === undefined) return track
    if (hint?.basis !== 'audio-stream') {
      return savedDuration === undefined
        ? { ...track, original_duration: hintedDuration, end_time: hintedDuration }
        : track
    }
    const start = typeof track.start_time === 'number' && Number.isFinite(track.start_time)
      ? Math.max(0, track.start_time)
      : 0
    const savedEnd = coerceTimelineDurationHint(track.end_time) ?? savedDuration ?? hintedDuration
    const savedWasFullSource = track.end_time === undefined || (
      coerceTimelineDurationHint(track.original_duration) !== undefined &&
      Math.abs(savedEnd - (track.original_duration ?? savedEnd)) <= 1e-6
    )
    const end = savedWasFullSource ? hintedDuration : Math.min(savedEnd, hintedDuration)
    if (end <= start) return track
    return { ...track, original_duration: hintedDuration, end_time: end }
  }
  const audioTracks = (timelineConfig.timeline.audio_tracks ?? []).map(applyAudioHint)
  const audio = timelineConfig.timeline.audio ? applyAudioHint(timelineConfig.timeline.audio) : timelineConfig.timeline.audio
  let effectiveClips = clips
  if (authoritativeVideoPresent && timelineConfig.timeline.magnet_enabled === true) {
    let cursor = 0
    const packed = new Map<number, number>()
    clips.map((clip, index) => ({ clip, index }))
      .filter(({ clip }) => (clip.track_index ?? 0) === 0)
      .sort((left, right) => (left.clip.order ?? left.index) - (right.clip.order ?? right.index))
      .forEach(({ clip, index }) => { packed.set(index, cursor); cursor += wireEntryDuration(clip) ?? 0 })
    effectiveClips = clips.map((clip, index) => packed.has(index) ? { ...clip, start_offset: packed.get(index) } : clip)
  }
  const nextTimeline = {
    ...timelineConfig.timeline,
    clips: effectiveClips,
    ...(timelineConfig.timeline.audio_tracks !== undefined ? { audio_tracks: audioTracks } : {}),
    ...(timelineConfig.timeline.audio !== undefined ? { audio } : {}),
  }
  const nextMediaEnd = timelineMediaEnd(nextTimeline)
  const savedTotal = coerceTimelineDurationHint(timelineConfig.timeline.total_duration)
  const savedTotalIsDerived = savedTotal === undefined || originalMediaEnd === undefined || Math.abs(savedTotal - originalMediaEnd) <= 1e-6
  const totalDuration = nextMediaEnd !== undefined && savedTotalIsDerived ? nextMediaEnd : savedTotal
  const config: TimelineRenderConfig = {
    ...timelineConfig,
    timeline: { ...nextTimeline, ...(totalDuration !== undefined ? { total_duration: totalDuration } : {}) },
  }
  return {
    config,
    changed: JSON.stringify(config) !== JSON.stringify(timelineConfig),
    authoritativeVideoIds,
    legacyVideoIds,
    missingVideoIds,
  }
}

export function timelineClipDurationOrDefault(value: number | null | undefined): number {
  return coerceTimelineDurationHint(value) ?? DEFAULT_TIMELINE_CLIP_DURATION_S
}

export interface TimelineAudioClipReference {
  id: string
  path: string
  name?: string
  duration?: number
  audioKind?: TimelineAudioKind
  role?: TimelineAudioClip['role']
}

export function buildInitialTimelineAudioClips(
  audioRefs: readonly TimelineAudioClipReference[],
): TimelineAudioClip[] {
  let nextStartOffset = 0
  return audioRefs.map((audioRef) => {
    const duration = timelineClipDurationOrDefault(audioRef.duration)
    const startOffset = nextStartOffset
    const audioKind = audioRef.audioKind ?? audioKindForLegacyRole(audioRef.role) ?? 'audio'
    nextStartOffset += duration
    return {
      id: audioRef.id,
      sourceNodeId: audioRef.id,
      sourcePath: audioRef.path,
      sourceRef: `${audioRef.id}.out_audio`,
      label: audioRef.name?.trim() || fallbackAudioLabelForKind(audioKind),
      audioKind,
      originalDuration: duration,
      trimStart: 0,
      trimEnd: 0,
      effectiveDuration: duration,
      volume: 0.8,
      muted: false,
      startOffset,
      role: audioRef.role,
      speed: 1.0,
      trackIndex: 0,
    }
  })
}

const NATIVE_AUDIO_CLIP_ID_PREFIX = 'native-audio:'
const EXTRACTED_NATIVE_AUDIO_CLIP_ID_PREFIX = 'extracted-native-audio:'

export function nativeAudioClipId(videoClipId: string): string {
  return `${NATIVE_AUDIO_CLIP_ID_PREFIX}${videoClipId}`
}

export function videoClipIdFromNativeAudioClipId(clipId: string): string | undefined {
  if (!clipId.startsWith(NATIVE_AUDIO_CLIP_ID_PREFIX)) return undefined
  const videoClipId = clipId.slice(NATIVE_AUDIO_CLIP_ID_PREFIX.length)
  return videoClipId.length > 0 ? videoClipId : undefined
}

export function extractedNativeAudioClipId(videoClipId: string): string {
  return `${EXTRACTED_NATIVE_AUDIO_CLIP_ID_PREFIX}${videoClipId}`
}

export function videoClipIdFromExtractedNativeAudioClipId(clipId: string | null | undefined): string | undefined {
  if (!clipId?.startsWith(EXTRACTED_NATIVE_AUDIO_CLIP_ID_PREFIX)) return undefined
  const sourceIdWithOptionalSplitSuffix = clipId.slice(EXTRACTED_NATIVE_AUDIO_CLIP_ID_PREFIX.length)
  const videoClipId = sourceIdWithOptionalSplitSuffix.replace(/-split-\d+$/, '')
  return videoClipId.length > 0 ? videoClipId : undefined
}

export function sourceVideoClipIdForExtractedNativeAudioClip(
  clip: Pick<TimelineAudioClip, 'id' | 'audioKind' | 'role' | 'sourceVideoClipId'>,
): string | undefined {
  if (!isOriginalAudio(clip)) return undefined
  return clip.sourceVideoClipId ?? videoClipIdFromExtractedNativeAudioClipId(clip.id)
}

export function isExtractedNativeAudioClip(
  clip: Pick<TimelineAudioClip, 'id' | 'audioKind' | 'role' | 'sourceVideoClipId'>,
): boolean {
  return sourceVideoClipIdForExtractedNativeAudioClip(clip) !== undefined
}

export function hasExtractedNativeAudioForVideo(
  audioClips: readonly Pick<TimelineAudioClip, 'id' | 'audioKind' | 'role' | 'sourceVideoClipId'>[],
  videoClipId: string,
): boolean {
  return audioClips.some((clip) => sourceVideoClipIdForExtractedNativeAudioClip(clip) === videoClipId)
}

function timelineAudioRangesOverlap(
  left: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>,
  right: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>,
): boolean {
  const leftStart = left.startOffset
  const leftEnd = left.startOffset + left.effectiveDuration
  const rightStart = right.startOffset
  const rightEnd = right.startOffset + right.effectiveDuration
  return leftStart < rightEnd - AUDIO_GAP_EPSILON_S && rightStart < leftEnd - AUDIO_GAP_EPSILON_S
}

export function chooseExtractedNativeAudioTrackPlacement(
  audioClips: readonly TimelineAudioClip[],
  candidate: Pick<TimelineAudioClip, 'startOffset' | 'effectiveDuration'>,
): { trackIndex: number; insertedOriginalTrack: boolean } {
  const extractedClips = audioClips.filter(isExtractedNativeAudioClip)
  const originalTrackCount =
    extractedClips.length > 0
      ? Math.max(...extractedClips.map((clip) => clip.trackIndex)) + 1
      : 0

  for (let trackIndex = 0; trackIndex < originalTrackCount; trackIndex++) {
    const overlapsTrack = extractedClips.some(
      (clip) => clip.trackIndex === trackIndex && timelineAudioRangesOverlap(clip, candidate),
    )
    if (!overlapsTrack) {
      return { trackIndex, insertedOriginalTrack: false }
    }
  }

  return { trackIndex: originalTrackCount, insertedOriginalTrack: true }
}

export function coerceTimelineFadeColor(value: string | null | undefined): 'black' | 'white' | undefined {
  if (value === null || value === undefined) return undefined
  if (value === 'black' || value === 'white') return value
  throw new Error(`Invalid timeline fade_color: ${value}`)
}

// RFC-1035: observation point for the restore path where it used to go blind —
// clips reconstructed from the wire vs. clips dropped because their base is
// missing. Gated by VITE_NAD_DEBUG (``dw:*``). Feature tag ``video-editor``
// (docs/FEATURE_TAGS.md), concern ``timeline-restore``.
const restoreDebug = createDebug('dw:video-editor:timeline-restore')
const autoPlacementLog = createNadDebug('dw:timeline-restore:auto-placement')
let lastAutoPlacementSignature = ''

/**
 * Place restored clips that carry no `start_offset` exactly like the renderer
 * (`video_worker/operations.py::_parse_timeline_render`) and
 * `timelineRenderConfigDuration`: sort by the wire `order`, defaulting to the
 * wire index when `order` is absent (ties keep wire order); each such clip
 * starts at the cumulative trimmed duration of every clip before it.
 *
 * The Director writes cut-down edits this way (trims + order, no offsets).
 * Falling back to the connected input's position instead kept each trimmed clip
 * in its UNTRIMMED slot, leaving gaps that previewed as black frames
 * (Workplace post 1653931126364184, T293966362).
 */
function placeClipsWithoutStartOffset(
  clips: TimelineClip[],
  autoPlaced: ReadonlySet<TimelineClip>,
  renderOrder: ReadonlyMap<TimelineClip, { order: number; wireIndex: number }>,
): TimelineClip[] {
  if (autoPlaced.size === 0) return clips
  const sortKey = (clip: TimelineClip) => {
    const key = renderOrder.get(clip)
    if (!key) throw new Error(`Timeline restore: no render order for clip ${clip.id}`)
    return key
  }
  const ordered = clips
    .map((clip) => ({ clip, ...sortKey(clip) }))
    .sort((a, b) => a.order - b.order || a.wireIndex - b.wireIndex)
  const placedStart = new Map<TimelineClip, number>()
  let cumulative = 0
  for (const { clip } of ordered) {
    if (autoPlaced.has(clip)) placedStart.set(clip, cumulative)
    cumulative += clip.effectiveDuration
  }
  const placed = clips.map((clip) => {
    const startOffset = placedStart.get(clip)
    return startOffset === undefined ? clip : { ...clip, startOffset }
  })
  // parseTimelineRenderConfig runs on every preview/modal re-render; log each
  // distinct placement once. Scalars only: the NAD transport summarizes arrays.
  const autoPlacedInOrder = ordered.filter(({ clip }) => placedStart.has(clip))
  const signature = autoPlacedInOrder
    .map(({ clip }) => `${clip.id}@${placedStart.get(clip)!.toFixed(3)}+${clip.effectiveDuration.toFixed(3)}`)
    .join(',')
  if (signature !== lastAutoPlacementSignature) {
    lastAutoPlacementSignature = signature
    autoPlacementLog({
      event: 'timeline-restore:clips-auto-placed',
      rule: 'cumulative-trimmed-duration-by-order',
      autoPlacedCount: autoPlaced.size,
      explicitCount: clips.length - autoPlaced.size,
      clipIds: autoPlacedInOrder.map(({ clip }) => clip.id).join(','),
      startOffsets: autoPlacedInOrder.map(({ clip }) => placedStart.get(clip)!.toFixed(3)).join(','),
      effectiveDurations: autoPlacedInOrder.map(({ clip }) => clip.effectiveDuration.toFixed(3)).join(','),
      timelineEnd: Number(cumulative.toFixed(3)),
    })
  }
  return placed
}

/**
 * Stable signature of the edge-connected source-id set (video + audio).
 *
 * T279076566: only a change to THIS set is a genuine edge topology change. The
 * timeline diverging from the edge set — e.g. the user deleted a clip whose
 * source edge is still connected — leaves the key unchanged, so a subsequent
 * `allNodes`/status-mirror churn that re-runs the merge does NOT reset the undo
 * history. Computed from the edge-resolved `initialClips`/`initialAudioClips`
 * (their `sourceNodeId`s), NOT from the current timeline clips.
 */
export function computeEdgeSourceKey(
  videoClips: readonly { sourceNodeId: string }[],
  audioClips: readonly { sourceNodeId: string }[],
): string {
  return JSON.stringify({
    v: [...new Set(videoClips.map((c) => c.sourceNodeId))].sort(),
    a: [...new Set(audioClips.map((c) => c.sourceNodeId))].sort(),
  })
}

/** Path-independent signature of the actual graph edges feeding the editor. */
export function computeConnectedSourceKey(
  videoSourceIds: readonly string[],
  audioSourceIds: readonly string[],
): string {
  return JSON.stringify({
    v: [...new Set(videoSourceIds)].sort(),
    a: [...new Set(audioSourceIds)].sort(),
  })
}

interface UpdatePendingConnectedSourceIdsInput {
  previousConnectedSourceIds: ReadonlySet<string>
  connectedSourceIds: ReadonlySet<string>
  resolvedSourceIds: ReadonlySet<string>
  pendingSourceIds: ReadonlySet<string>
}

/** Track only newly-connected sources whose media path has not resolved yet. */
export function updatePendingConnectedSourceIds({
  previousConnectedSourceIds,
  connectedSourceIds,
  resolvedSourceIds,
  pendingSourceIds,
}: UpdatePendingConnectedSourceIdsInput): Set<string> {
  const next = new Set(
    [...pendingSourceIds].filter(
      (sourceId) => connectedSourceIds.has(sourceId) && !resolvedSourceIds.has(sourceId),
    ),
  )
  for (const sourceId of connectedSourceIds) {
    if (!previousConnectedSourceIds.has(sourceId) && !resolvedSourceIds.has(sourceId)) {
      next.add(sourceId)
    }
  }
  return next
}

interface ComputeMaterializableSourceIdsInput {
  existingSourceIds: ReadonlySet<string>
  newlyConnectedSourceIds: ReadonlySet<string>
  newlyResolvedPendingSourceIds: ReadonlySet<string>
}

/** Sources that should be added during a topology reconcile. */
export function computeMaterializableSourceIds({
  existingSourceIds,
  newlyConnectedSourceIds,
  newlyResolvedPendingSourceIds,
}: ComputeMaterializableSourceIdsInput): Set<string> {
  return new Set(
    [...newlyConnectedSourceIds, ...newlyResolvedPendingSourceIds].filter(
      (sourceId) => !existingSourceIds.has(sourceId),
    ),
  )
}

export function timelineStringTraceHash(input: string): string {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return `${input.length}:${(hash >>> 0).toString(16)}`
}

export function timelineConfigTraceHash(config: TimelineRenderConfig | null | undefined): string {
  return timelineStringTraceHash(config ? JSON.stringify(config) : '')
}

/** Exact schema-owned creation root, before any user or Director timeline authoring. */
export function isPristineTimelineRenderConfig(
  config: TimelineRenderConfig | null | undefined,
): boolean {
  if (config?.operation !== 'timeline_render' || !config.timeline) return false
  const keys = Object.keys(config.timeline).sort()
  return (
    keys.length === 2 &&
    keys[0] === 'audio_tracks' &&
    keys[1] === 'clips' &&
    Array.isArray(config.timeline.clips) &&
    config.timeline.clips.length === 0 &&
    Array.isArray(config.timeline.audio_tracks) &&
    config.timeline.audio_tracks.length === 0
  )
}

export interface TimelineAudioInterval {
  startS: number
  endS: number
}

export interface BuildSelectiveAudioFillClipsInput {
  sourceNodeId: string
  sourcePath: string
  sourceRef?: string
  originalDuration: number
  label?: string
  audioKind?: TimelineAudioKind
  role?: TimelineAudioClip['role']
  volume: number
  muted?: boolean
  startOffset: number
  trackIndex: number
  timelineEndS: number
  existingAudioClips: TimelineAudioClip[]
  idFactory?: () => string
}

function makeTimelineAudioClipId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `audio-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function mergeIntervals(intervals: TimelineAudioInterval[]): TimelineAudioInterval[] {
  const sorted = intervals
    .filter((interval) => interval.endS - interval.startS > AUDIO_GAP_EPSILON_S)
    .sort((a, b) => a.startS - b.startS || a.endS - b.endS)

  const merged: TimelineAudioInterval[] = []
  for (const interval of sorted) {
    const previous = merged[merged.length - 1]
    if (!previous || interval.startS > previous.endS + AUDIO_GAP_EPSILON_S) {
      merged.push({ ...interval })
      continue
    }
    previous.endS = Math.max(previous.endS, interval.endS)
  }
  return merged
}

export function getAudioCoverageIntervals(
  audioClips: TimelineAudioClip[],
  rangeStartS: number,
  rangeEndS: number,
): TimelineAudioInterval[] {
  if (rangeEndS <= rangeStartS) return []
  return mergeIntervals(
    audioClips
      .filter((clip) => !clip.muted)
      .map((clip) => ({
        startS: Math.max(rangeStartS, clip.startOffset),
        endS: Math.min(rangeEndS, getTimelineClipEnd(clip)),
      }))
  )
}

export function getAudioGapIntervals(
  coverage: TimelineAudioInterval[],
  rangeStartS: number,
  rangeEndS: number,
): TimelineAudioInterval[] {
  if (rangeEndS <= rangeStartS) return []

  const gaps: TimelineAudioInterval[] = []
  let cursor = rangeStartS
  for (const interval of mergeIntervals(coverage)) {
    if (interval.startS > cursor + AUDIO_GAP_EPSILON_S) {
      gaps.push({ startS: cursor, endS: interval.startS })
    }
    cursor = Math.max(cursor, interval.endS)
  }
  if (rangeEndS > cursor + AUDIO_GAP_EPSILON_S) {
    gaps.push({ startS: cursor, endS: rangeEndS })
  }
  return gaps
}

/**
 * Build audio clip fragments for a background/master track.
 *
 * If the target timeline already has unmuted audio coverage, the new source is
 * split into the uncovered regions so it does not stack over scene-level music
 * or speech. When there is no existing audio coverage, this preserves the old
 * behavior by returning one continuous clip capped to the video timeline end.
 */
export function buildSelectiveAudioFillClips({
  sourceNodeId,
  sourcePath,
  sourceRef,
  originalDuration,
  label,
  audioKind,
  role,
  volume,
  muted = false,
  startOffset,
  trackIndex,
  timelineEndS,
  existingAudioClips,
  idFactory = makeTimelineAudioClipId,
}: BuildSelectiveAudioFillClipsInput): TimelineAudioClip[] {
  const sourceDuration = Math.max(0, originalDuration)
  const rangeStartS = Math.max(0, startOffset)
  const rangeEndS = Number.isFinite(timelineEndS)
    ? Math.max(rangeStartS, timelineEndS)
    : rangeStartS + sourceDuration

  if (sourceDuration <= AUDIO_GAP_EPSILON_S || rangeEndS <= rangeStartS + AUDIO_GAP_EPSILON_S) {
    return []
  }

  const coverage = getAudioCoverageIntervals(existingAudioClips, rangeStartS, rangeEndS)
  const gaps = getAudioGapIntervals(coverage, rangeStartS, rangeEndS)

  const clips: TimelineAudioClip[] = []
  const effectiveAudioKind = audioKind ?? audioKindForLegacyRole(role)
  let consumedSourceS = 0
  for (const gap of gaps) {
    if (consumedSourceS >= sourceDuration - AUDIO_GAP_EPSILON_S) break

    const requestedDuration = gap.endS - gap.startS
    const segmentDuration = Math.min(requestedDuration, sourceDuration - consumedSourceS)
    if (segmentDuration <= AUDIO_GAP_EPSILON_S) continue

    const sourceInS = consumedSourceS
    const sourceOutS = sourceInS + segmentDuration
    clips.push({
      id: idFactory(),
      sourceNodeId,
      sourcePath,
      sourceRef,
      label: label ? `${label}${gaps.length > 1 ? ` (${clips.length + 1})` : ''}` : fallbackAudioLabelForKind(effectiveAudioKind),
      audioKind: effectiveAudioKind,
      originalDuration: sourceDuration,
      trimStart: sourceInS,
      trimEnd: Math.max(0, sourceDuration - sourceOutS),
      effectiveDuration: segmentDuration,
      volume,
      muted,
      startOffset: gap.startS,
      role,
      speed: 1.0,
      trackIndex: Math.max(0, trackIndex),
    })
    consumedSourceS = sourceOutS
  }

  return clips
}

export function refreshTimelineAudioClipSourceDuration(
  clip: TimelineAudioClip,
  realDuration: number,
): TimelineAudioClip {
  const nextOriginalDuration = Number.isFinite(realDuration) ? Math.max(0, realDuration) : clip.originalDuration
  if (Math.abs(nextOriginalDuration - clip.originalDuration) <= AUDIO_GAP_EPSILON_S) {
    return clip
  }

  const speed = Number.isFinite(clip.speed) && clip.speed > AUDIO_GAP_EPSILON_S ? clip.speed : 1.0
  const oldSourceSpanS = Math.max(0, clip.effectiveDuration * speed)
  const wasFullSource =
    clip.trimStart <= AUDIO_GAP_EPSILON_S &&
    clip.trimEnd <= AUDIO_GAP_EPSILON_S &&
    Math.abs(oldSourceSpanS - clip.originalDuration) <= AUDIO_GAP_EPSILON_S

  if (wasFullSource) {
    return {
      ...clip,
      originalDuration: nextOriginalDuration,
      trimEnd: 0,
      effectiveDuration: nextOriginalDuration / speed,
    }
  }

  const trimStart = Math.min(Math.max(0, clip.trimStart), nextOriginalDuration)
  const sourceOutS = Math.min(nextOriginalDuration, trimStart + oldSourceSpanS)
  return {
    ...clip,
    originalDuration: nextOriginalDuration,
    trimStart,
    trimEnd: Math.max(0, nextOriginalDuration - sourceOutS),
    effectiveDuration: Math.max(0, sourceOutS - trimStart) / speed,
  }
}

/**
 * Format time in HH:MM:SS:FF format
 * @param seconds Time in seconds
 * @param fps Frames per second (default 30)
 */
export function formatTime(seconds: number, fps: number = 30): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const frames = Math.floor((seconds % 1) * fps)

  const hh = hours.toString().padStart(2, '0')
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  const ff = frames.toString().padStart(2, '0')

  if (hours > 0) {
    return `${hh}:${mm}:${ss}:${ff}`
  }
  return `${mm}:${ss}:${ff}`
}

/**
 * Format time in simple MM:SS format
 */
export function formatTimeSimple(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  return `${mm}:${ss}`
}

/**
 * Convert pixels to seconds based on zoom level
 */
export function pixelsToSeconds(pixels: number, zoomLevel: number): number {
  return pixels / zoomLevel
}

/**
 * Convert seconds to pixels based on zoom level
 */
export function secondsToPixels(seconds: number, zoomLevel: number): number {
  return seconds * zoomLevel
}

export function getTimelineClipEnd(clip: TimelineClip | TimelineAudioClip): number {
  return Math.max(0, clip.startOffset) + Math.max(0, clip.effectiveDuration)
}

export function computeTimelineDuration(
  clips: TimelineClip[],
  audioClips: TimelineAudioClip[] = []
): number {
  const videoEnd = clips.reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  const audioEnd = audioClips.reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  return Math.max(videoEnd, audioEnd)
}

export function timelineStateAudioOnlyTailSeconds(
  clips: TimelineClip[],
  audioClips: TimelineAudioClip[],
): number {
  if (clips.length === 0) return 0
  const visualEnd = clips.reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  const audioEnd = audioClips
    .filter((clip) => !clip.muted && clip.volume > 0)
    .reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  return Math.max(0, audioEnd - visualEnd)
}

function wireMediaDuration(item: {
  start_time?: number
  end_time?: number
  original_duration?: number
  speed?: number
}): number | undefined {
  const start = item.start_time ?? 0
  const end = item.end_time ?? item.original_duration
  const speed = item.speed ?? 1
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(speed) || end === undefined || speed <= 0) {
    return undefined
  }
  return Math.max(0, end - Math.max(0, start)) / speed
}

export function timelineConfigAudioOnlyTailSeconds(
  config: TimelineRenderConfig | null | undefined,
): number {
  const timeline = config?.timeline
  if (!timeline?.clips?.length) return 0
  let automaticVideoOffset = 0
  let visualEnd = 0
  for (const clip of [...timeline.clips].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    const duration = wireMediaDuration(clip)
    if (duration === undefined) return 0
    const offset = typeof clip.start_offset === 'number'
      ? Math.max(0, clip.start_offset)
      : automaticVideoOffset
    visualEnd = Math.max(visualEnd, offset + duration)
    automaticVideoOffset += duration
  }
  const audioTracks = timeline.audio_tracks?.length
    ? timeline.audio_tracks
    : timeline.audio
      ? [timeline.audio]
      : []
  const mutedAudioTracks = new Set(
    (timeline.audio_tracks_meta ?? []).filter((track) => track.muted).map((track) => track.track_index),
  )
  const audioCursorByTrack = new Map<number, number>()
  let audioEnd = 0
  for (const track of audioTracks) {
    const duration = wireMediaDuration(track)
    if (duration === undefined) continue
    const trackIndex = track.track_index ?? 0
    const automaticAudioOffset = audioCursorByTrack.get(trackIndex) ?? 0
    const offset = typeof track.start_offset === 'number'
      ? Math.max(0, track.start_offset)
      : automaticAudioOffset
    if (!mutedAudioTracks.has(trackIndex) && !track.muted && (track.volume ?? 1) > 0) {
      audioEnd = Math.max(audioEnd, offset + duration)
    }
    audioCursorByTrack.set(
      trackIndex,
      Math.max(automaticAudioOffset, offset + duration),
    )
  }
  return Math.max(0, audioEnd - visualEnd)
}

/**
 * Calculate clip position (in pixels) on timeline
 */
export function getClipPosition(clip: TimelineClip, clips: TimelineClip[], zoomLevel: number): number {
  const startOffset = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return secondsToPixels(startOffset, zoomLevel)
}

/**
 * Calculate clip width (in pixels) on timeline
 */
export function getClipWidth(clip: TimelineClip, zoomLevel: number): number {
  return secondsToPixels(clip.effectiveDuration, zoomLevel)
}

/**
 * Find clip at a given time position
 */
export function getClipAtTime(clips: TimelineClip[], time: number): TimelineClip | null {
  return [...clips]
    .sort((a, b) => a.trackIndex - b.trackIndex || a.order - b.order)
    .find((clip) => time >= clip.startOffset && time < getTimelineClipEnd(clip)) ?? null
}

/**
 * Get time offset within a clip (relative to clip's trimmed start)
 */
export function getTimeOffsetInClip(clip: TimelineClip, clips: TimelineClip[], globalTime: number): number {
  const startOffset = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return Math.max(0, Math.min(globalTime - startOffset, clip.effectiveDuration))
}

/**
 * Snap detection threshold (in pixels). Used by clip-move drag and clip-edge drag.
 */
export const SNAP_THRESHOLD = 8

/**
 * Find snap position for a clip being moved (whole-clip drag).
 * Returns the snap position in pixels if within threshold, null otherwise.
 * Always snaps to timeline start (0) and to other clips' start/end pixels.
 */
export function findSnapPosition(
  _draggedClip: TimelineClip,
  otherClips: TimelineClip[],
  currentPosition: number,
  zoomLevel: number
): number | null {
  const snapTargets: number[] = [0]

  otherClips.forEach((clip) => {
    const clipStart = getClipPosition(clip, otherClips, zoomLevel)
    const clipEnd = clipStart + getClipWidth(clip, zoomLevel)
    snapTargets.push(clipStart, clipEnd)
  })

  let closestSnap: number | null = null
  let minDistance = SNAP_THRESHOLD

  snapTargets.forEach((target) => {
    const distance = Math.abs(currentPosition - target)
    if (distance < minDistance) {
      minDistance = distance
      closestSnap = target
    }
  })

  return closestSnap
}

/**
 * Edge-drag snap (Issue 2). Snap targets:
 *  - The red playhead cursor pixel (if provided)
 *  - The start and end pixels of OTHER clips on the SAME track as the dragged clip
 *
 * Returns the snap pixel within SNAP_THRESHOLD of the dragged edge, or null.
 */
export function findEdgeSnapPosition(
  edgePixel: number,
  sameTrackOtherClips: TimelineClip[],
  zoomLevel: number,
  playheadPixel: number | null
): { snapPx: number; target: 'playhead' | 'clip-edge' } | null {
  const candidates: { px: number; target: 'playhead' | 'clip-edge' }[] = []
  if (playheadPixel !== null && Number.isFinite(playheadPixel)) {
    candidates.push({ px: playheadPixel, target: 'playhead' })
  }
  sameTrackOtherClips.forEach((c) => {
    const start = getClipPosition(c, sameTrackOtherClips, zoomLevel)
    const end = start + getClipWidth(c, zoomLevel)
    candidates.push({ px: start, target: 'clip-edge' })
    candidates.push({ px: end, target: 'clip-edge' })
  })

  let best: { snapPx: number; target: 'playhead' | 'clip-edge' } | null = null
  let minDistance = SNAP_THRESHOLD
  candidates.forEach((c) => {
    const distance = Math.abs(edgePixel - c.px)
    if (distance < minDistance) {
      minDistance = distance
      best = { snapPx: c.px, target: c.target }
    }
  })
  return best
}

/**
 * Calculate new clip order based on drag position
 */
export function calculateNewOrder(
  draggedClipId: string,
  newPosition: number,
  clips: TimelineClip[],
  zoomLevel: number
): number {
  const draggedClip = clips.find((c) => c.id === draggedClipId)
  if (!draggedClip) return 0

  const otherClips = clips.filter((c) => c.id !== draggedClipId)

  // Find which position this clip should be at based on pixel position
  let cumulativeTime = 0
  let newOrder = 0

  for (let i = 0; i < otherClips.length; i++) {
    const clipPosition = secondsToPixels(cumulativeTime, zoomLevel)
    if (newPosition < clipPosition) {
      return newOrder
    }
    cumulativeTime += otherClips[i].effectiveDuration
    newOrder++
  }

  return newOrder
}

/**
 * Generate timeline ruler tick marks
 * Returns array of { position: pixels, label: string, isMajor: boolean }
 */
export interface RulerTick {
  position: number
  label: string
  isMajor: boolean
}

export function generateRulerTicks(
  totalDuration: number,
  zoomLevel: number,
  viewportWidth: number
): RulerTick[] {
  const ticks: RulerTick[] = []

  // Determine tick interval based on zoom level
  let majorInterval: number
  let minorInterval: number

  if (zoomLevel >= 100) {
    // Zoomed in: major every 1s, minor every 0.2s
    majorInterval = 1
    minorInterval = 0.2
  } else if (zoomLevel >= 50) {
    // Medium: major every 3s, minor every 1s
    majorInterval = 3
    minorInterval = 1
  } else {
    // Zoomed out: major every 10s, minor every 2s
    majorInterval = 10
    minorInterval = 2
  }

  const maxTime = Math.max(totalDuration, pixelsToSeconds(viewportWidth, zoomLevel))

  // Generate minor ticks
  for (let t = 0; t <= maxTime; t += minorInterval) {
    const isMajor = t % majorInterval === 0
    ticks.push({
      position: secondsToPixels(t, zoomLevel),
      label: isMajor ? formatTimeSimple(t) : '',
      isMajor,
    })
  }

  return ticks
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Calculate clip time boundaries (start and end in global timeline)
 */
export function getClipTimeBoundaries(
  clip: TimelineClip,
  clips: TimelineClip[]
): { startTime: number; endTime: number } {
  const startTime = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return { startTime, endTime: startTime + clip.effectiveDuration }
}

type TrackPositionedClip = TimelineClip | TimelineAudioClip

export const TIMELINE_OVERLAP_EPSILON = 1e-6

function clipSortOrder(clip: TrackPositionedClip): number {
  return 'order' in clip && typeof clip.order === 'number' ? clip.order : Number.MAX_SAFE_INTEGER
}

/**
 * Enforce the timeline invariant that clips in the same track do not overlap.
 *
 * If an edited clip is provided and it ties another clip's start, the edited clip
 * keeps that requested start and the colliding clip is shifted right. If the
 * requested start falls inside an earlier clip, the edited clip is clamped to the
 * earlier clip's end and later collisions cascade right.
 */
export function preventSameTrackOverlaps(
  clips: TimelineClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TimelineClip[]
export function preventSameTrackOverlaps(
  clips: TimelineAudioClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TimelineAudioClip[]
export function preventSameTrackOverlaps(
  clips: TrackPositionedClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TrackPositionedClip[] {
  const allowNegativeForEdited = options?.allowNegativeForEditedClip === true
  const byTrack = new Map<number, Array<{ clip: TrackPositionedClip; index: number }>>()

  clips.forEach((clip, index) => {
    const trackIndex = typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0
    const trackClips = byTrack.get(trackIndex) ?? []
    trackClips.push({ clip: { ...clip, trackIndex }, index })
    byTrack.set(trackIndex, trackClips)
  })

  const normalized = [...clips]

  for (const trackClips of byTrack.values()) {
    const sorted = [...trackClips].sort((a, b) => {
      // Sort uses raw startOffset (may be negative) so edited clip stays leftmost
      const startDelta = a.clip.startOffset - b.clip.startOffset
      if (Math.abs(startDelta) > TIMELINE_OVERLAP_EPSILON) return startDelta
      if (a.clip.id === editedClipId && b.clip.id !== editedClipId) return -1
      if (b.clip.id === editedClipId && a.clip.id !== editedClipId) return 1
      return clipSortOrder(a.clip) - clipSortOrder(b.clip) || a.index - b.index
    })

    let nextAvailableStart = -Infinity
    let initialized = false
    for (const { clip, index } of sorted) {
      const isEdited = clip.id === editedClipId
      const allowNegativeHere = allowNegativeForEdited && isEdited
      const baseStart = allowNegativeHere ? clip.startOffset : Math.max(0, clip.startOffset)
      const requestedStart = baseStart
      const startOffset = initialized && requestedStart < nextAvailableStart - TIMELINE_OVERLAP_EPSILON
        ? nextAvailableStart
        : requestedStart
      const effectiveDuration = Math.max(0, clip.effectiveDuration)

      normalized[index] =
        startOffset === clip.startOffset &&
        effectiveDuration === clip.effectiveDuration &&
        clip.trackIndex === normalized[index].trackIndex
          ? clip
          : { ...clip, startOffset, effectiveDuration }
      nextAvailableStart = startOffset + effectiveDuration
      initialized = true
    }
  }

  return normalized
}

export function normalizeVideoClips(clips: TimelineClip[]): TimelineClip[] {
  let cumulative = 0
  const normalized = [...clips]
    .sort((a, b) => a.order - b.order)
    .map((clip, index) => {
      const startOffset = typeof clip.startOffset === 'number' ? clip.startOffset : cumulative
      cumulative += clip.effectiveDuration
      return {
        ...clip,
        order: typeof clip.order === 'number' ? clip.order : index,
        startOffset,
        trackIndex: typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0,
        speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
        volume: typeof clip.volume === 'number' ? Math.max(0, Math.min(MAX_VOLUME_AMPLITUDE, clip.volume)) : 1.0,
        muted: typeof clip.muted === 'boolean' ? clip.muted : false,
      }
    })
  return preventSameTrackOverlaps(normalized)
}

export function normalizeAudioClips(clips: TimelineAudioClip[]): TimelineAudioClip[] {
  return preventSameTrackOverlaps(
    clips.map((clip) => ({
      ...clip,
      speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
      startOffset: typeof clip.startOffset === 'number' ? clip.startOffset : 0,
      trackIndex: typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0,
    }))
  )
}

function timelineVideoSourceRef(clip: TimelineClip): string {
  return clip.sourceRef ?? clip.sourcePath
}

function timelineAudioSourceRef(clip: TimelineAudioClip): string {
  if (sourceVideoClipIdForExtractedNativeAudioClip(clip)) {
    return clip.sourcePath
  }
  return clip.sourceRef ?? clip.sourcePath
}

/**
 * A symbolic timeline source ref is a graph-backed `<nodeId>.out_<port>` pointer
 * (see #2674). Literal asset paths (uploads, extract/split-audio WAVs) do NOT
 * match. This is the single source of truth for the predicate — the modal's
 * `classifyTimelineSourceRef` reuses it so build/parse/reconstruct stay aligned.
 * RFC-1035.
 */
export const SYMBOLIC_TIMELINE_SOURCE_REF_PATTERN = /^[^./]+\.out_[A-Za-z0-9_]+$/

export function isSymbolicTimelineSourceRef(
  sourcePath: string | null | undefined,
): boolean {
  if (!sourcePath) return false
  return SYMBOLIC_TIMELINE_SOURCE_REF_PATTERN.test(sourcePath.trim())
}

/**
 * Deterministic, collision-free id for a clip reconstructed from the wire when
 * no live input edge resolves it (RFC-1035). Prefers the persisted
 * `source_node_id`, then the persisted clip `id`, then a stable hash of the
 * literal source path. Reconstructed clips have no live edge, so a synthetic
 * id cannot collide with a connected input's node id.
 */
function deriveReconstructedSourceNodeId(
  sourceNodeId: string | null | undefined,
  clipId: string | null | undefined,
  sourcePath: string,
): string {
  if (sourceNodeId && sourceNodeId.trim() !== '') return sourceNodeId
  if (clipId && clipId.trim() !== '') return clipId
  return `recon-${timelineStringTraceHash(sourcePath)}`
}

/** Best-effort display label for a reconstructed timeline-only clip (RFC-1035). */
function timelineSourceLabel(sourcePath: string): string {
  const basename = sourcePath.split('/').pop()
  return basename && basename.trim() !== '' ? basename : sourcePath
}

function fallbackAudioLabelForLegacyRole(role: LegacyTimelineAudioRole | null | undefined): string {
  if (role === 'music') return 'Music'
  if (role === 'sfx') return 'Sound effect'
  if (role === 'replacement') return 'Voice'
  return 'Audio'
}

function coerceTimelineAudioKind(value: string | null | undefined): TimelineAudioKind | undefined {
  return isTimelineAudioKind(value) ? value : undefined
}

function deriveAudioClipLabel(
  label: string | null | undefined,
  audioKind: TimelineAudioKind | undefined,
  role: LegacyTimelineAudioRole | null | undefined,
  sourcePath: string,
): string {
  const trimmed = typeof label === 'string' ? label.trim() : ''
  if (trimmed) return trimmed
  if (audioKind && audioKind !== 'audio') return fallbackAudioLabelForKind(audioKind)
  if (isLegacyTimelineAudioRole(role)) return fallbackAudioLabelForLegacyRole(role)
  return timelineSourceLabel(sourcePath)
}

/**
 * Shared wire-trim math for restore (RFC-1035). Given the branch-selected
 * `originalDuration`, converts the wire `start_time`/`end_time` (absolute source
 * trims) into `trimStart`/`trimEnd`/`effectiveDuration`. Identical in all four
 * parse branches; only the `originalDuration` selection differs per branch.
 */
function deriveWireTrim(
  originalDuration: number,
  startTime: number | undefined,
  endTime: number | undefined,
  speed: number,
): { trimStart: number; trimEnd: number; effectiveDuration: number } {
  const trimStart = startTime ?? 0
  const trimEnd = endTime != null ? Math.max(0, originalDuration - endTime) : 0
  const effectiveDuration = Math.max(0, (originalDuration - trimStart - trimEnd) / speed)
  return { trimStart, trimEnd, effectiveDuration }
}

function connectedSourceOriginalDuration(
  kind: 'video' | 'audio',
  id: string | undefined,
  baseDuration: number,
  savedDuration: number | undefined,
  endTime: number | undefined,
  liveSourceConnected: boolean,
): number {
  const baseLooksPlaceholder =
    Math.abs(baseDuration - DEFAULT_TIMELINE_CLIP_DURATION_S) <= AUDIO_GAP_EPSILON_S
  const sourceDuration = liveSourceConnected && !baseLooksPlaceholder
    ? baseDuration
    : (savedDuration ?? baseDuration)
  const originalDuration = endTime != null ? Math.max(sourceDuration, endTime) : sourceDuration
  if (
    savedDuration != null &&
    Number.isFinite(baseDuration) &&
    Math.abs(savedDuration - baseDuration) > 0.01
  ) {
    restoreDebug(
      '%s connected source duration mismatch id=%s saved=%s upstream=%s restored=%s',
      kind,
      id,
      savedDuration,
      baseDuration,
      originalDuration,
    )
  }
  return originalDuration
}

function ttsPlannedEndOffsetForWire(
  clip: Pick<TimelineAudioClip, 'audioKind' | 'plannedEndOffset'>,
): number | undefined {
  return clip.audioKind === 'speech' ? clip.plannedEndOffset : undefined
}

/**
 * Resolve the real (probed) duration for a timeline audio clip.
 *
 * RFC-1035 (RC2b): a literal timeline-owned audio clip (extract/split WAV)
 * carries the source VIDEO's `sourceNodeId`, so only graph-backed (symbolic
 * `<id>.out_*`) clips may inherit a duration by node id; literal clips key by
 * their own `id`. Single source of truth reused by the modal and the canvas
 * preview player so the two never drift.
 */
export function resolveAudioClipRealDuration(
  clip: Pick<TimelineAudioClip, 'sourceRef' | 'sourcePath' | 'sourceNodeId' | 'id'>,
  durations: Map<string, number>,
): number | undefined {
  return isSymbolicTimelineSourceRef(clip.sourceRef ?? clip.sourcePath)
    ? durations.get(clip.sourceNodeId) ?? durations.get(clip.id)
    : durations.get(clip.id)
}

/**
 * Apply loaded/probed real durations to a timeline's video + audio clips.
 *
 * Single source of truth for the duration-apply logic shared by the
 * TimelineEditorModal and TimelinePreviewPlayer effects (RFC-1035). Handles
 * audio-only timelines (video-clip-free) and routes each audio clip's duration
 * through `resolveAudioClipRealDuration` (RC2b: literal timeline-owned audio
 * keys by its own id; graph-backed clips inherit by node id). Audio duration
 * refresh uses `refreshTimelineAudioClipSourceDuration` (the trim-preserving
 * path). `durations` only ever holds real (>0) values, so a `real != null`
 * guard preserves the current truthiness behavior.
 *
 * Returns `{ changed: false, ... }` (with the inputs echoed back) when nothing
 * needed updating, so callers can skip a redundant state write.
 */
/** Main-track placement is shared by edits and asynchronous source reconciliation. */
export function packMainTrack(clips: TimelineClip[]): TimelineClip[] {
  let cursor = 0
  const packed = new Map<string, TimelineClip>()
  for (const clip of clips.filter((clip) => clip.trackIndex === 0).sort((a, b) => a.startOffset - b.startOffset || a.order - b.order)) {
    packed.set(clip.id, Math.abs(clip.startOffset - cursor) > 1e-6 ? { ...clip, startOffset: cursor } : clip)
    cursor += clip.effectiveDuration
  }
  return clips.map((clip) => packed.get(clip.id) ?? clip)
}

/** Each new reference starts after the preceding main-track clip, never after audio. */
export function appendMainTrackReferences(clips: TimelineClip[], references: TimelineClip[], magnetic: boolean): TimelineClip[] {
  let cursor = computeTimelineDuration(clips.filter((clip) => clip.trackIndex === 0), [])
  let order = clips.reduce((max, clip) => Math.max(max, clip.order), -1) + 1
  const appended = references.map((clip) => {
    const next = { ...clip, order: order++, startOffset: cursor, trackIndex: 0 }
    cursor += clip.effectiveDuration
    return next
  })
  const merged = [...clips, ...appended]
  return magnetic ? packMainTrack(merged) : merged
}

export function applyRealDurationsToTimeline(
  clips: TimelineClip[],
  audioClips: TimelineAudioClip[],
  durations: Map<string, number>,
): { changed: boolean; clips: TimelineClip[]; audioClips: TimelineAudioClip[]; totalDuration: number } {
  const videoNeeds = clips.some((c) => {
    // Source identity wins over the stable UI id: time edits keep the clip id
    // but replace its source. Falling back to that id revives an old probe.
    const real = durations.get(c.sourceNodeId || c.id)
    return real != null && real !== c.originalDuration
  })
  const audioNeeds = audioClips.some((c) => {
    const real = resolveAudioClipRealDuration(c, durations)
    return real != null && real !== c.originalDuration
  })
  if (!videoNeeds && !audioNeeds) {
    return { changed: false, clips, audioClips, totalDuration: computeTimelineDuration(clips, audioClips) }
  }
  const updatedClips = clips.map((c) => {
    const real = durations.get(c.sourceNodeId || c.id)
    if (real == null || real === c.originalDuration) return c
    // `trimEnd` is an amount measured from the end of the OLD duration, which
    // may be a placeholder. Keep a trimmed clip's source end point (e.g. a
    // Director `end_time`) instead of the amount, so the probe can't stretch or
    // shrink the clip; an untrimmed clip still grows to the full real source.
    const sourceEnd = c.trimEnd > 0 ? Math.min(real, c.originalDuration - c.trimEnd) : real
    const trimEnd = Math.max(0, real - sourceEnd)
    return {
      ...c,
      originalDuration: real,
      trimEnd,
      effectiveDuration: Math.max(0, (real - c.trimStart - trimEnd) / c.speed),
    }
  })
  const updatedAudioClips = audioClips.map((c) => {
    const real = resolveAudioClipRealDuration(c, durations)
    return real != null && real !== c.originalDuration
      ? refreshTimelineAudioClipSourceDuration(c, real)
      : c
  })
  return {
    changed: true,
    clips: updatedClips,
    audioClips: updatedAudioClips,
    totalDuration: computeTimelineDuration(updatedClips, updatedAudioClips),
  }
}

/**
 * Build a TimelineRenderConfig from the current timeline state.
 * Shared by TimelineEditorModal (render button) and VideoEditorNode (save-on-close).
 */
export function buildTimelineRenderConfig(timelineState: TimelineState): TimelineRenderConfig {
  return {
    operation: 'timeline_render',
    timeline: {
      clips: timelineState.clips.map((clip) => {
        const fadeColor = coerceTimelineFadeColor(clip.fadeColor)
        return {
          id: clip.id,
          source_path: timelineVideoSourceRef(clip),
          // RFC-1035: persist the untrimmed source length + producing node id so
          // reopen reconstructs the exact end-trim and can rebuild timeline-only
          // clips without a live input edge.
          original_duration: clip.originalDuration,
          source_node_id: clip.sourceNodeId,
          start_time: clip.trimStart,
          end_time: clip.originalDuration - clip.trimEnd,
          order: clip.order,
          speed: clip.speed,
          start_offset: clip.startOffset,
          track_index: clip.trackIndex,
          volume: clip.volume,
          muted: clip.muted,
          audio_fade_in_s: Math.min(clip.effectiveDuration, clip.audioFadeInS ?? 0),
          audio_fade_out_s: Math.min(clip.effectiveDuration, clip.audioFadeOutS ?? 0),
          ...(clip.adjustments !== undefined ? { adjustments: clip.adjustments } : {}),
          ...(clip.speedCurve !== undefined ? { speed_curve: clip.speedCurve } : {}),
          ...(fadeColor ? { fade_color: fadeColor } : {}),
          // Persist fade-out so the backend renders the visualized fade.
          // Clamp to clip's effective duration on the way out — the worker
          // re-clamps too but a sane wire value keeps replay deterministic.
          fade_out_s: Math.max(
            0,
            Math.min(clip.effectiveDuration, clip.fadeOutS ?? 0)
          ),
          fade_in_s: Math.max(
            0,
            Math.min(clip.effectiveDuration, clip.fadeInS ?? 0)
          ),
        }
      }),
      audio:
        timelineState.audioClips.length === 1
          ? (() => {
              const clip = timelineState.audioClips[0]
              const plannedEndOffset = ttsPlannedEndOffsetForWire(clip)
              return {
                source_path: timelineAudioSourceRef(clip),
                // RFC-1035: self-contained restore (see clips[] above).
                original_duration: clip.originalDuration,
                source_node_id: clip.sourceNodeId,
                ...(clip.label !== undefined ? { label: clip.label } : {}),
                ...(clip.audioKind !== undefined ? { audio_kind: clip.audioKind } : {}),
                start_time: clip.trimStart,
                end_time: clip.originalDuration - clip.trimEnd,
                volume: clip.volume,
                ...(plannedEndOffset !== undefined
                  ? { planned_end_offset: plannedEndOffset }
                  : {}),
                fade_in_s: Math.min(clip.effectiveDuration, clip.audioFadeInS ?? 0),
                fade_out_s: Math.min(clip.effectiveDuration, clip.audioFadeOutS ?? 0),
              }
            })()
          : undefined,
      audio_tracks:
        timelineState.audioClips.length > 0
          ? timelineState.audioClips
              .map((c) => {
                const plannedEndOffset = ttsPlannedEndOffsetForWire(c)
                return {
                  id: c.id,
                  source_path: timelineAudioSourceRef(c),
                  // RFC-1035: self-contained restore (see clips[] above).
                  original_duration: c.originalDuration,
                  source_node_id: c.sourceNodeId,
                  ...(c.label !== undefined ? { label: c.label } : {}),
                  ...(c.audioKind !== undefined ? { audio_kind: c.audioKind } : {}),
                  start_time: c.trimStart,
                  end_time: c.originalDuration - c.trimEnd,
                  volume: c.volume,
                  start_offset: c.startOffset,
                  ...(plannedEndOffset !== undefined ? { planned_end_offset: plannedEndOffset } : {}),
                  ...(c.sourceVideoClipId !== undefined ? { source_video_clip_id: c.sourceVideoClipId } : {}),
                  speed: c.speed,
                  track_index: c.trackIndex,
                  muted: c.muted,
                  ...(c.splitGroupId !== undefined ? { split_group_id: c.splitGroupId } : {}),
                  ...(c.peakDbfs !== undefined ? { peak_dbfs: c.peakDbfs } : {}),
                  fade_in_s: Math.min(c.effectiveDuration, c.audioFadeInS ?? 0),
                  fade_out_s: Math.min(c.effectiveDuration, c.audioFadeOutS ?? 0),
                }
              })
          : undefined,
      video_tracks_meta: Array.from(timelineState.mutedVideoTracks)
        .sort((a, b) => a - b)
        .map((trackIndex) => ({ track_index: trackIndex, muted: true })),
      audio_tracks_meta: Array.from(timelineState.mutedAudioTracks)
        .sort((a, b) => a - b)
        .map((trackIndex) => ({ track_index: trackIndex, muted: true })),
      total_duration: timelineState.totalDuration,
      video_track_count: timelineState.videoTrackCount,
      audio_track_count: timelineState.audioTrackCount,
      magnet_enabled: timelineState.magnetEnabled,
      ...(timelineState.textOverlays ? { text_overlays: timelineState.textOverlays } : {}),
      ...(timelineState.textFormat ? { text_format: timelineState.textFormat } : {}),
    },
  }
}

export function parseTimelineRenderConfig(
  config: TimelineRenderConfig,
  initialClips: TimelineClip[],
  initialAudioClips: TimelineAudioClip[],
  connectedSources?: {
    connectedVideoSourceIds: ReadonlySet<string>
    connectedAudioSourceIds: ReadonlySet<string>
  },
): TimelineState {
  const timeline = config.timeline
  if (!timeline) return {
    clips: initialClips,
    audioClips: initialAudioClips,
    playheadPosition: 0,
    zoomLevel: 50,
    isPlaying: false,
    selectedClipIds: new Set<string>(),
    totalDuration: computeTimelineDuration(initialClips, initialAudioClips),
    videoTrackCount: 1,
    audioTrackCount: 1,
    copiedClip: null,
    copiedClipType: null,
    mutedVideoTracks: new Set<number>(),
    mutedAudioTracks: new Set<number>(),
    magnetEnabled: true,
  }
  const clipsBySourceRef = new Map<string, TimelineClip>()
  for (const clip of initialClips) {
    clipsBySourceRef.set(clip.sourcePath, clip)
    clipsBySourceRef.set(`${clip.sourceNodeId}.out_video`, clip)
  }
  const audioBySourceRef = new Map<string, TimelineAudioClip>()
  for (const clip of initialAudioClips) {
    audioBySourceRef.set(clip.sourcePath, clip)
    audioBySourceRef.set(`${clip.sourceNodeId}.out_audio`, clip)
  }

  const clips: TimelineClip[] = []
  // Clips saved without `start_offset` (Director trims) get the renderer's
  // placement after the loop; see placeClipsWithoutStartOffset.
  const autoPlacedClips = new Set<TimelineClip>()
  // The renderer's sort key: wire `order`, else the wire index (not the
  // connected input's order, which `base.order` carries).
  const clipRenderOrder = new Map<TimelineClip, { order: number; wireIndex: number }>()
  for (const [wireIndex, wc] of (timeline.clips ?? []).entries()) {
    const base = wc.source_path ? clipsBySourceRef.get(wc.source_path) : undefined
    const speed = wc.speed ?? 1.0
    if (base) {
      const symbolicSourceNodeId = wc.source_node_id ?? wc.source_path?.split('.out_')[0]
      const liveSourceConnected =
        !!symbolicSourceNodeId &&
        connectedSources?.connectedVideoSourceIds.has(symbolicSourceNodeId) === true
      // Connected symbolic refs use the live upstream duration when available.
      // Disconnected restore keeps the saved/end-time value as a guard for
      // older configs and placeholder bases.
      const originalDuration = connectedSourceOriginalDuration(
        'video',
        wc.id ?? base.id,
        base.originalDuration,
        wc.original_duration,
        wc.end_time,
        liveSourceConnected,
      )
      const { trimStart, trimEnd, effectiveDuration } = deriveWireTrim(
        originalDuration,
        wc.start_time,
        wc.end_time,
        speed,
      )
      const restoredClip: TimelineClip = {
        ...base,
        id: wc.id ?? base.id,
        originalDuration,
        trimStart,
        trimEnd,
        effectiveDuration,
        order: wc.order ?? base.order,
        speed,
        startOffset: wc.start_offset ?? 0,
        trackIndex: wc.track_index ?? 0,
        volume: wc.volume ?? base.volume,
        muted: wc.muted ?? false,
        fadeOutS: wc.fade_out_s ?? 0,
        fadeInS: wc.fade_in_s ?? 0,
        adjustments: wc.adjustments,
        speedCurve: wc.speed_curve,
        audioFadeInS: wc.audio_fade_in_s,
        audioFadeOutS: wc.audio_fade_out_s,
        fadeColor: coerceTimelineFadeColor(wc.fade_color),
      }
      clips.push(restoredClip)
      clipRenderOrder.set(restoredClip, { order: wc.order ?? wireIndex, wireIndex })
      if (wc.start_offset == null) autoPlacedClips.add(restoredClip)
      continue
    }
    // base missing. A symbolic `<id>.out_*` ref whose input is gone is a
    // genuinely disconnected graph-backed clip → drop (never freeze it to a
    // stale literal path; preserves #2674). A LITERAL asset path is a
    // timeline-only clip (upload / extract-audio source) → reconstruct from the
    // wire instead of silently dropping it (RC1). RFC-1035.
    const symbolicSourceNodeId = wc.source_node_id ?? wc.source_path?.split('.out_')[0]
    const connectedSymbolicSource =
      !!wc.source_path &&
      isSymbolicTimelineSourceRef(wc.source_path) &&
      !!symbolicSourceNodeId &&
      connectedSources?.connectedVideoSourceIds.has(symbolicSourceNodeId) === true
    if (!wc.source_path || (isSymbolicTimelineSourceRef(wc.source_path) && !connectedSymbolicSource)) {
      restoreDebug('video-clip dropped (base missing, symbolic/empty ref) id=%s ref=%s', wc.id, wc.source_path)
      continue
    }
    const originalDuration = wc.original_duration ?? wc.end_time ?? 0
    const { trimStart, trimEnd, effectiveDuration } = deriveWireTrim(
      originalDuration,
      wc.start_time,
      wc.end_time,
      speed,
    )
    const reconSourceNodeId = deriveReconstructedSourceNodeId(
      wc.source_node_id,
      wc.id,
      wc.source_path,
    )
    restoreDebug(
      'video-clip reconstructed (base missing, literal path) id=%s path=%s origDur=%s trimStart=%s trimEnd=%s',
      wc.id,
      wc.source_path,
      originalDuration,
      trimStart,
      trimEnd,
    )
    const reconstructedClip: TimelineClip = {
      id: wc.id ?? reconSourceNodeId,
      sourceNodeId: connectedSymbolicSource && symbolicSourceNodeId
        ? symbolicSourceNodeId
        : reconSourceNodeId,
      sourcePath: wc.source_path,
      sourceRef: connectedSymbolicSource ? wc.source_path : undefined,
      originalDuration,
      trimStart,
      trimEnd,
      effectiveDuration,
      order: wc.order ?? clips.length,
      startOffset: wc.start_offset ?? 0,
      trackIndex: wc.track_index ?? 0,
      label: timelineSourceLabel(wc.source_path),
      speed,
      volume: wc.volume ?? 1.0,
      muted: wc.muted ?? false,
      fadeOutS: wc.fade_out_s ?? 0,
      fadeInS: wc.fade_in_s ?? 0,
      adjustments: wc.adjustments,
      speedCurve: wc.speed_curve,
      audioFadeInS: wc.audio_fade_in_s,
      audioFadeOutS: wc.audio_fade_out_s,
      fadeColor: coerceTimelineFadeColor(wc.fade_color),
    }
    clips.push(reconstructedClip)
    clipRenderOrder.set(reconstructedClip, { order: wc.order ?? wireIndex, wireIndex })
    if (wc.start_offset == null) autoPlacedClips.add(reconstructedClip)
  }
  const placedClips = placeClipsWithoutStartOffset(clips, autoPlacedClips, clipRenderOrder)
  clips.splice(0, clips.length, ...placedClips)

  const configuredAudioTracks = timeline.audio_tracks ?? (timeline.audio ? [timeline.audio] : [])
  const audioClips: TimelineAudioClip[] = []
  const automaticAudioEndByTrack = new Map<number, number>()
  const restoreAudioStartOffset = (
    trackIndex: number,
    explicitStartOffset: number | undefined,
    effectiveDuration: number,
  ): number => {
    const cursor = automaticAudioEndByTrack.get(trackIndex) ?? 0
    if (explicitStartOffset !== undefined) {
      automaticAudioEndByTrack.set(
        trackIndex,
        Math.max(cursor, explicitStartOffset + effectiveDuration),
      )
      return explicitStartOffset
    }
    const startOffset = cursor
    automaticAudioEndByTrack.set(trackIndex, startOffset + effectiveDuration)
    return startOffset
  }
  configuredAudioTracks.forEach((wa, idx) => {
    const base = wa.source_path ? audioBySourceRef.get(wa.source_path) : undefined
    const speed = wa.speed ?? 1.0
    const audioKind = coerceTimelineAudioKind(wa.audio_kind)
    const legacyRole = isLegacyTimelineAudioRole(wa.role) ? wa.role : undefined
    const sourceVideoClipId =
      wa.source_video_clip_id ?? videoClipIdFromExtractedNativeAudioClipId(wa.id)
    const restoredAudioKind = audioKind
      ?? (legacyRole === 'original' && sourceVideoClipId
        ? 'extracted_original_audio'
        : audioKindForLegacyRole(legacyRole))
    if (base) {
      const symbolicSourceNodeId = wa.source_node_id ?? wa.source_path?.split('.out_')[0]
      const liveSourceConnected =
        !!symbolicSourceNodeId &&
        connectedSources?.connectedAudioSourceIds.has(symbolicSourceNodeId) === true
      const originalDuration = connectedSourceOriginalDuration(
        'audio',
        wa.id ?? base.id,
        base.originalDuration,
        wa.original_duration,
        wa.end_time,
        liveSourceConnected,
      )
      const { trimStart, trimEnd, effectiveDuration } = deriveWireTrim(
        originalDuration,
        wa.start_time,
        wa.end_time,
        speed,
      )
      const trackIndex = wa.track_index ?? 0
      audioClips.push({
        ...base,
        id: wa.id ?? (idx === 0 ? base.id : `${base.id}:cfg-${idx}`),
        originalDuration,
        trimStart,
        trimEnd,
        effectiveDuration,
        label: deriveAudioClipLabel(wa.label, restoredAudioKind ?? base.audioKind, legacyRole ?? base.role, base.sourcePath),
        audioKind: restoredAudioKind ?? base.audioKind,
        volume: wa.volume ?? base.volume,
        muted: wa.muted ?? false,
        startOffset: restoreAudioStartOffset(trackIndex, wa.start_offset, effectiveDuration),
        plannedEndOffset: wa.planned_end_offset,
        role: legacyRole ?? base.role,
        speed,
        trackIndex,
        sourceVideoClipId,
        splitGroupId: wa.split_group_id ?? undefined,
        peakDbfs: wa.peak_dbfs ?? undefined,
        audioFadeInS: wa.fade_in_s,
        audioFadeOutS: wa.fade_out_s,
      })
      return
    }
    // base missing. Symbolic ref with no live input → drop (#2674). Literal
    // asset path (extract/split-audio WAV, upload) → reconstruct from the wire
    // so the clip survives reopen without a connected audio input port (RC1).
    // RFC-1035.
    const symbolicSourceNodeId = wa.source_node_id ?? wa.source_path?.split('.out_')[0]
    const connectedSymbolicSource =
      !!wa.source_path &&
      isSymbolicTimelineSourceRef(wa.source_path) &&
      !!symbolicSourceNodeId &&
      connectedSources?.connectedAudioSourceIds.has(symbolicSourceNodeId) === true
    if (!wa.source_path || (isSymbolicTimelineSourceRef(wa.source_path) && !connectedSymbolicSource)) {
      restoreDebug('audio-clip dropped (base missing, symbolic/empty ref) id=%s ref=%s', wa.id, wa.source_path)
      return
    }
    const originalDuration = wa.original_duration ?? wa.end_time ?? 0
    const { trimStart, trimEnd, effectiveDuration } = deriveWireTrim(
      originalDuration,
      wa.start_time,
      wa.end_time,
      speed,
    )
    const trackIndex = wa.track_index ?? 0
    const reconSourceNodeId = deriveReconstructedSourceNodeId(
      wa.source_node_id,
      wa.id,
      wa.source_path,
    )
    restoreDebug(
      'audio-clip reconstructed (base missing, literal path) id=%s path=%s origDur=%s trimStart=%s trimEnd=%s',
      wa.id,
      wa.source_path,
      originalDuration,
      trimStart,
      trimEnd,
    )
    audioClips.push({
      id: wa.id ?? (idx === 0 ? reconSourceNodeId : `${reconSourceNodeId}:cfg-${idx}`),
      sourceNodeId: connectedSymbolicSource && symbolicSourceNodeId
        ? symbolicSourceNodeId
        : reconSourceNodeId,
      sourcePath: wa.source_path,
      sourceRef: connectedSymbolicSource ? wa.source_path : undefined,
      label: deriveAudioClipLabel(wa.label, restoredAudioKind, legacyRole, wa.source_path),
      audioKind: restoredAudioKind,
      originalDuration,
      trimStart,
      trimEnd,
      effectiveDuration,
      volume: wa.volume ?? 1.0,
      muted: wa.muted ?? false,
      startOffset: restoreAudioStartOffset(trackIndex, wa.start_offset, effectiveDuration),
      plannedEndOffset: wa.planned_end_offset,
      role: legacyRole,
      speed,
      trackIndex,
      sourceVideoClipId,
      splitGroupId: wa.split_group_id ?? undefined,
      peakDbfs: wa.peak_dbfs ?? undefined,
      audioFadeInS: wa.fade_in_s,
      audioFadeOutS: wa.fade_out_s,
    })
  })

  const mutedVideoTracks = new Set<number>()
  for (const meta of timeline.video_tracks_meta ?? []) {
    if (meta.muted && meta.track_index != null) mutedVideoTracks.add(meta.track_index)
  }
  const mutedAudioTracks = new Set<number>()
  for (const meta of timeline.audio_tracks_meta ?? []) {
    if (meta.muted && meta.track_index != null) mutedAudioTracks.add(meta.track_index)
  }

  const mediaDuration = computeTimelineDuration(clips, audioClips)
  const textDuration = (timeline.text_overlays ?? []).reduce((maxEnd, overlay) => {
    const start = Number.isFinite(overlay.start_seconds) ? Math.max(0, overlay.start_seconds) : 0
    const duration = Number.isFinite(overlay.duration_seconds)
      ? Math.max(0, overlay.duration_seconds)
      : 0
    return Math.max(maxEnd, start + duration)
  }, 0)
  const totalDuration = Math.max(timeline.total_duration ?? 0, mediaDuration, textDuration)

  return {
    clips,
    audioClips,
    playheadPosition: 0,
    zoomLevel: 50,
    isPlaying: false,
    selectedClipIds: new Set<string>(),
    totalDuration,
    videoTrackCount: timeline.video_track_count ?? Math.max(1, ...clips.map(c => c.trackIndex + 1)),
    audioTrackCount: timeline.audio_track_count ?? Math.max(1, ...audioClips.map(c => c.trackIndex + 1)),
    copiedClip: null,
    copiedClipType: null,
    mutedVideoTracks,
    mutedAudioTracks,
    magnetEnabled: timeline.magnet_enabled ?? true,
    textOverlays: timeline.text_overlays,
    textFormat: timeline.text_format,
  }
}
